import * as React from "react";
import { Avatar as MaterialAvatar, AvatarProps } from "@mui/material";
import { DocumentedComponent } from "./DocumentedComponent";

interface ExtendedAvatarProps extends AvatarProps {
    color?: "red" | "orange" | "yellow" | "green" | "blue" | "darkblue";
    fallback?: string;
}

function stringToColor(string: string) {
    let hash = 0;

    /* eslint-disable no-bitwise */
    for (let i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';

    for (let i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */

    return color;
}

function stringAvatar(_name: string) {
    const name = _name ?? "Anonymous User";

    return {
        sx: {
            bgcolor: stringToColor(name)
        },
        children: `${name.split(' ')?.[0]?.[0] ?? ""}${name.split(' ')?.[1]?.[0] ?? name.split(' ')?.[0]?.[1] ?? ""}`.toUpperCase(),
    };
}

const Avatar: React.FC<ExtendedAvatarProps> = (props) => <MaterialAvatar className={`${props?.color}`} {...props} {...(props?.fallback ? stringAvatar(props?.fallback) : {})} />;

(Avatar as DocumentedComponent).metadata = {
    description: "Displays an avatar.  A variety of shapes and colors can be used here.  Use the `src` prop for an image-based avatar.  If the `fallback` property is provided, the initials + background color will automatically be calculated from the provided text.  Alternatively, any content can be passed through via the `children` prop.",
    attributes: [
        { name: "color", description: "The color of the avatar.  Optional - defaults to the default for the current theme (gray).", type: "string", options: ["red", "orange", "yellow", "green", "blue", "darkblue"] },
        { name: "variant", description: "Determines how the Avatar is displayed.  Optional - defaults to 'circular'.", type: "string", options: ["circular", "rounded", "square"] },
        { name: "src", description: "The URL of the image to display.  Supports the 'data:' URL scheme.  Optional - no default.", type: "string" },
        { name: "fallback", description: "The fallback text to use for calculating the initials + background color, if no `src` is provided.  Optional - no default.", type: "string" },
    ]
};

export default Avatar;